import { useEffect, useState } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Info, Sparkles, Image as ImageIcon } from "lucide-react";
import { getErrorMessage } from "@/lib/utils";
import OfferTemplateForm, {
  DISCOUNT_STEPS,
  DOLLAR_STEPS,
  OFFER_TEMPLATES,
  SMART_ELIGIBLE_TEMPLATES,
  cleanPayload,
  emptyPayload,
  validatePayload,
} from "./OfferTemplateForm";

/**
 * Create or edit one offer (spec 9).
 *
 * The tier rules are made VISIBLE rather than discovered. Smart Offers are Gold-only
 * (9.4) and only apply to the two discount templates; Optimisation Strength needs Gold
 * AND Smart (9.5). Rather than let a merchant fill a form and collect a 403, the
 * controls disable themselves and say why - the tier table is a selling point, so
 * "Smart Offers are a Gold feature" is better copy than an error.
 *
 * The backend validates all of it regardless (campaign_rules.validate_offer_config);
 * this is about not wasting the merchant's time.
 */

// spec 9.5, with the spec's own descriptions as help text - they are already
// merchant-facing copy.
// Offer Category was removed from this form in round 4 (G-13, decision D3). It never
// reached a customer - category_id appears nowhere in offer_serving.py or the served
// payload - so its only effect was to filter the merchant's own list, from an
// admin-managed table that has never been seeded.
//
// The FIELD stays on the Offer model and the /offer-categories endpoints stay up, so
// stored values survive and putting the control back is all it would take to reverse
// this. See D3 for the scope: business categories, which drive venue matching, are a
// different system and are untouched.

const STRENGTHS = [
  { value: "CONSERVATIVE", label: "Conservative", help: "Stay close to your preferred value." },
  { value: "BALANCED", label: "Balanced", help: "Optimise where confidence is high.", recommended: true },
  { value: "AGGRESSIVE", label: "Aggressive", help: "Use the full approved range to maximise performance." },
];

/**
 * `campaignContent` is the Content ALREADY ATTACHED to this campaign, not the library
 * (round 7, SP-26 / decision D6). Offering the library would let a merchant point an
 * offer at an image the campaign does not carry, and serving would quietly fall back to
 * the campaign creative with nothing to explain why.
 */
export default function OfferEditor({
  open,
  onOpenChange,
  campaignTier,
  offer,          // null when creating
  prefill,        // { template, payload } from an AI suggestion; create-mode only
  campaignContent = [],   // the campaign's own Content, for the creative picker (D6)
  categories = [], // OfferCategory[]; the owner fetches them, this stays presentational
  source = "TIER",
  onSave,         // async (body) => void; throws to keep the dialog open
  saving = false,
}) {
  const editing = Boolean(offer);
  const isGold = campaignTier === "GOLD";

  const [template, setTemplate] = useState("PERCENTAGE_DISCOUNT");
  const [payload, setPayload] = useState(() => emptyPayload("PERCENTAGE_DISCOUNT"));
  const [offerType, setOfferType] = useState("FIXED");
  const [bounds, setBounds] = useState({ preferred: "", minimum: "", maximum: "" });
  const [strength, setStrength] = useState("BALANCED");
  const [categoryId, setCategoryId] = useState("");
  // Which of the campaign's creatives this offer renders (D6). Empty means "use
  // the campaign's", which is what pick_offer_creative falls back to.
  const [contentId, setContentId] = useState("");
  const [error, setError] = useState(null);

  // Boost offers are always Fixed (8.1), so the toggle is not offered for them at all.
  const isBoost = source === "DISCOVERY_BOOST";
  const templateAllowsSmart = SMART_ELIGIBLE_TEMPLATES.includes(template);
  const smartAvailable = isGold && templateAllowsSmart && !isBoost;
  const isSmart = offerType === "SMART";

  useEffect(() => {
    if (!open) return;
    if (offer) {
      setTemplate(offer.template);
      setPayload({ ...emptyPayload(offer.template), ...(offer.payload || {}) });
      setOfferType(offer.offer_type || "FIXED");
      setBounds(
        offer.smart_bounds
          ? {
              preferred: offer.smart_bounds.preferred ?? "",
              minimum: offer.smart_bounds.minimum ?? "",
              maximum: offer.smart_bounds.maximum ?? "",
            }
          : { preferred: "", minimum: "", maximum: "" }
      );
      setStrength(offer.optimisation_strength || "BALANCED");
      setCategoryId(offer.category_id || "");
      setContentId(offer.content_id || "");
    } else if (prefill?.template) {
      // A suggestion opens the editor pre-filled. The merchant still reviews and
      // confirms every field before anything is saved, which is what makes inferring
      // a template from AI prose acceptable here.
      setTemplate(prefill.template);
      setPayload({ ...emptyPayload(prefill.template), ...(prefill.payload || {}) });
      setOfferType("FIXED");
      setBounds({ preferred: "", minimum: "", maximum: "" });
      setStrength("BALANCED");
      setCategoryId("");
      setContentId("");
    } else {
      setTemplate("PERCENTAGE_DISCOUNT");
      setPayload(emptyPayload("PERCENTAGE_DISCOUNT"));
      setOfferType("FIXED");
      setBounds({ preferred: "", minimum: "", maximum: "" });
      setStrength("BALANCED");
      setCategoryId("");
      setContentId("");
    }
    setError(null);
  }, [open, offer, prefill]);

  // Switching to a template that cannot be optimised must drop Smart, or the merchant
  // could submit a combination the backend rejects.
  useEffect(() => {
    if (isSmart && !templateAllowsSmart) setOfferType("FIXED");
  }, [template, templateAllowsSmart, isSmart]);

  const boundsError = () => {
    const { preferred, minimum, maximum } = bounds;
    if ([preferred, minimum, maximum].some((v) => String(v).trim() === "")) {
      return "Enter a preferred, minimum and maximum value.";
    }
    const [p, lo, hi] = [Number(preferred), Number(minimum), Number(maximum)];
    if ([p, lo, hi].some((n) => Number.isNaN(n))) return "Bounds must be numbers.";
    if (lo < 0) return "Bounds cannot be negative.";
    // Same rule the backend enforces (SmartBounds.ordered); checked here so the
    // merchant does not need a round-trip to learn the ordering.
    if (!(lo <= p && p <= hi)) return "Minimum must be at or below preferred, and preferred at or below maximum.";
    // `preferred` becomes the saved discount_percent or dollar_amount, so it has to be
    // one of the steps the backend accepts. The selectors cannot offer anything else,
    // but a prefill from an AI suggestion or an older record can (G-10).
    // Derived here rather than read from `unit`, which is declared further down the
    // component body. It works by closure today, but only because submit runs after
    // render; making it local removes the ordering dependency.
    const pct = template === "PERCENTAGE_DISCOUNT";
    const steps = pct ? DISCOUNT_STEPS : DOLLAR_STEPS;
    if (!steps.includes(p)) {
      return pct
        ? "Preferred must be a multiple of 5 between 10% and 95%."
        : `Preferred must be a multiple of $5 between $${steps[0]} and $${steps[steps.length - 1]}.`;
    }
    return null;
  };

  const submit = async () => {
    // A Smart offer has no single amount on screen - the range replaced it - so the
    // payload's required value comes from `preferred`. The backend requires
    // discount_percent / dollar_amount regardless of pricing model, and preferred is
    // where Booma starts before it moves within the bounds.
    const effectivePayload = { ...payload, template };
    if (isSmart) {
      const p = Number(bounds.preferred);
      if (template === "PERCENTAGE_DISCOUNT") effectivePayload.discount_percent = p;
      else if (template === "DOLLAR_DISCOUNT") effectivePayload.dollar_amount = p;
    }

    // Bounds first when Smart: validatePayload would otherwise complain about the amount
    // field, which is derived from the bounds the merchant has not filled in yet.
    if (isSmart) {
      const be = boundsError();
      if (be) return setError(be);
    }

    const payloadError = validatePayload(template, effectivePayload);
    if (payloadError) return setError(payloadError);

    const body = {
      template,
      payload: cleanPayload(effectivePayload),
      offer_type: offerType,
      source,
      // Still sent, so editing an offer that already has a category does not silently
      // clear it. Nothing sets it any more (G-13), so on a new offer this is null.
      category_id: categoryId || null,
      // null means "use the campaign creative" - pick_offer_creative's own fallback -
      // so clearing the selection is a real choice rather than a missing value (D6).
      content_id: contentId || null,
    };

    if (isSmart) {
      body.smart_bounds = {
        preferred: Number(bounds.preferred),
        minimum: Number(bounds.minimum),
        maximum: Number(bounds.maximum),
      };
      if (isGold) body.optimisation_strength = strength;
    }

    setError(null);
    try {
      await onSave(body);
    } catch (e) {
      // Surface the backend's message verbatim - spec 10.5 specifies some of this wording.
      // Through getErrorMessage, because `detail` is only a STRING on the business-rule
      // refusals. A 422 from request validation carries a LIST of Pydantic error objects,
      // and this rendered it straight into JSX below, so React threw "Objects are not
      // valid as a React child" and the whole page fell to the error boundary. That is
      // the blank error screen Joel hit on every Experience save (round 12, FP-19), and
      // it would have hidden the real message on any other validation failure too.
      setError(getErrorMessage(e?.response?.data, "Could not save the offer."));
    }
  };

  const unit = template === "PERCENTAGE_DISCOUNT" ? "%" : "$";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit offer" : "Add an offer"}</DialogTitle>
          <DialogDescription>
            {isBoost
              ? "A Discovery Boost offer. These appear throughout Discovery and never during a trip."
              : "Offers are created inside a campaign and belong to it."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* The creative, FIRST and exactly where Joel asked for it: "a section at the
              top asking to select creative under the Offers are created inside a
              campaign and belong to it text and above the booma smart offer/fixed offer
              container" (SP-26).

              Only what the campaign already carries. Hidden entirely when the campaign
              has one image or none, because there is no choice to make - an offer with
              no creative of its own renders the campaign's, which is the same result. */}
          {campaignContent.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Creative for this offer</Label>
              <div className="flex flex-wrap gap-2">
                {campaignContent.map((c) => {
                  const chosen = contentId === c.id;
                  const src = c.master_url && !c.master_url.startsWith("http") && !c.master_url.startsWith("data:")
                    ? `${API.replace(/\/api\/?$/, "")}${c.master_url}`
                    : c.master_url;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setContentId(chosen ? "" : c.id)}
                      title={c.name}
                      className={`relative h-16 w-16 rounded-md border-2 overflow-hidden bg-muted/40 transition-colors ${
                        chosen ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`offer-creative-${c.id}`}
                    >
                      {src ? (
                        <img src={src} alt={c.name} className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-muted-foreground/50 m-auto" />
                      )}
                      {chosen && (
                        <span className="absolute inset-x-0 bottom-0 bg-primary/90 text-[9px] text-primary-foreground py-0.5">
                          Chosen
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {contentId
                  ? "This offer shows the image you picked."
                  : campaignContent.length === 1
                    ? "Nothing picked, so this offer shows the campaign's image."
                    : `Nothing picked, so this offer shows whichever of the campaign's ${campaignContent.length} images the campaign uses. Click one to choose.`}
              </p>
            </div>
          )}

          {/* How it is priced comes FIRST, because everything below depends on it.
              Fixed and Smart are not variations of one form - they are alternatives, and
              the old order asked for an amount and then offered a pricing model that
              makes a single amount meaningless. */}
          {!isBoost && (
            <div className="space-y-2 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                {/* SP-27: "change how is it priced text to Offer Type". His note
                    collides with the label below, which was already "Offer type" - so
                    this takes the name and the template select becomes "Offer Template",
                    which is what the code has always called it. */}
                <Label className="text-xs text-muted-foreground">Offer Type</Label>
                {!smartAvailable && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Info className="h-3 w-3" />
                    {!isGold
                      ? "Smart Offers are a Gold feature"
                      : "Not optimisable in Version One"}
                  </Badge>
                )}
              </div>
              <RadioGroup
                value={offerType}
                onValueChange={setOfferType}
                className="space-y-2 pt-1"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="FIXED" id="type-fixed" className="mt-0.5" />
                  <Label htmlFor="type-fixed" className="font-normal cursor-pointer">
                    <span className="text-sm">Fixed</span>
                    <span className="block text-[11px] text-muted-foreground">
                      Every customer sees the same value.
                    </span>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="SMART" id="type-smart" className="mt-0.5"
                    disabled={!smartAvailable}
                  />
                  <Label
                    htmlFor="type-smart"
                    className={`font-normal ${smartAvailable ? "cursor-pointer" : "opacity-50"}`}
                  >
                    <span className="text-sm flex items-center gap-1">
                      <Sparkles className="h-3 w-3 text-yellow-600" />
                      Booma Smart Offer
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      Booma optimises within bounds you approve. You stay in control.
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* The Category control was here and is gone (round 4, G-13). See the note
              at the top of this file: it never reached a customer, so it asked the
              merchant a question with no consequence. */}

          {/* Offer Template, read only (round 9, TP-9).
              It was already locked when editing. Create was the gap, and it was a
              leftover from before SP-17: back then you opened a blank editor and chose
              a template inside it. Since SP-17 the templates ARE the way in, so the
              choice has been made before this renders, and a dropdown offering nine
              alternatives invites a change that silently empties the payload.

              To change it, remove the offer and pick another template. That is the
              instruction, and it is now the only route. */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Offer Template</Label>
            <div
              className="flex items-center gap-2 h-10 px-3 rounded-md border border-border bg-muted/40"
              data-testid="offer-template-fixed"
            >
              {(() => {
                const meta = OFFER_TEMPLATES.find((t) => t.value === template);
                const Icon = meta?.icon;
                return (
                  <>
                    {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <span className="text-sm font-medium">{meta?.label || template}</span>
                  </>
                );
              })()}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {editing
                ? "The template cannot be changed. Remove the offer and add a new one instead."
                : OFFER_TEMPLATES.find((t) => t.value === template)?.hint}
            </p>
          </div>

          {/* FIXED: the amount, who it applies to, and any conditions. */}
          {!isSmart && (
            <OfferTemplateForm template={template} payload={payload} onChange={setPayload} />
          )}

          {/* SMART: the range REPLACES the single amount. A Smart offer moves within
              bounds, so one fixed value is precisely the thing it is not - showing both
              was the incompatibility this reorder exists to remove. */}
          {isSmart && (
            <div className="space-y-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
              <div>
                <Label className="text-xs font-medium">
                  Approved range {unit === "%" ? "(discount)" : "(amount off)"}
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Booma may move the offer inside this range and nowhere else. It starts at
                  your preferred value.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["minimum", "Minimum", unit === "%" ? "15" : "5"],
                  ["preferred", "Preferred", unit === "%" ? "20" : "10"],
                  ["maximum", "Maximum", unit === "%" ? "30" : "15"],
                ].map(([key, label, placeholder]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      {label}
                    </Label>
                    {/* Both units are selectors over the same steps the fixed controls
                        use, and for a real reason: submit() derives the saved
                        discount_percent or dollar_amount from `preferred`, and the
                        backend validates that against the step list. A free-text 17,
                        or $12, would be refused for a field that is not on screen in
                        Smart mode at all.

                        The dollar half was free text until round 4 (G-10: "this applies
                        for both fixed and smart offers"), which was exactly that bug
                        waiting for the fixed control to be tightened. */}
                    <Select
                      value={bounds[key] === "" ? undefined : String(bounds[key])}
                      onValueChange={(v) => setBounds({ ...bounds, [key]: v })}
                    >
                      <SelectTrigger data-testid={`bound-${key}`}>
                        <SelectValue placeholder={unit === "%" ? `${placeholder}%` : `$${placeholder}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {(unit === "%" ? DISCOUNT_STEPS : DOLLAR_STEPS).map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {unit === "%" ? `${n}%` : `$${n}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimisation Strength (9.5) - Gold AND Smart, both required */}
          {isSmart && isGold && (
            <div className="space-y-2 rounded-lg border p-3">
              <Label className="text-xs text-muted-foreground">Optimisation strength</Label>
              <RadioGroup value={strength} onValueChange={setStrength} className="space-y-2 pt-1">
                {STRENGTHS.map((s) => (
                  <div key={s.value} className="flex items-start gap-2">
                    <RadioGroupItem value={s.value} id={`strength-${s.value}`} className="mt-0.5" />
                    <Label htmlFor={`strength-${s.value}`} className="font-normal cursor-pointer">
                      <span className="text-sm">
                        {s.label}
                        {s.recommended && (
                          <span className="text-muted-foreground font-normal"> (recommended)</span>
                        )}
                      </span>
                      <span className="block text-[11px] text-muted-foreground">{s.help}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}


          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editing ? "Save offer" : "Add offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
